import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import {
    Typography,
} from '@material-ui/core';

import HtmlContent from '../HtmlContent';
import {
    elementConditionStates, contentElemenIdPrefix, systemContentElementTypes,
} from '../../constants';

import TaskForm from './TaskForm';
import TaskElementLink from './TaskElementLink';
import { getElementConditionState } from '../../elementStateUtils';

const styles = theme => ({
    root: {
        position: 'relative',
        clear: 'both', // needed in case of 'float: left' from htmlcontent (image-alignment)
        width: '100%', // needed to allow sub-elements to overflow individualy (ex. table)
        flex: '0 0 100%', // Parent is Flex + Wrap but normal items should fill width

        '&+&': {
            marginTop: theme.spacing(8),
        },

        [theme.breakpoints.down('sm')]: {
            wordBreak: 'break-word', // to avoid width overflow on small screens. (using overflow: hidden will break sticky headers)

            '&+&': {
                marginBottom: theme.spacing(6),
            },
        },

        '&$selected': {
            border: `2px solid ${theme.palette.primary.main}`,
            padding: theme.spacing(1),
        },

        [`&.et_${systemContentElementTypes.box}`]: {
            background: theme.colors.highlightBackground,
            boxShadow: theme.shadows[3],
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(2),
            alignSelf: 'flex-start',

            '&$collapsed $htmlContent': {
                '&:before': {
                    background: `linear-gradient(transparent 120px, ${theme.colors.dimBackground})`,
                },
            },
        },

        [`&.et_${systemContentElementTypes.card}`]: {
            flex: '0 0 auto',
            width: '31%', // Does not work properly on all screens with just flex-basis 31%
            padding: theme.spacing(1),
            background: theme.colors.dimBackground,
            borderRadius: theme.spacing(0.5),

            [theme.breakpoints.down('xs')]: {
                flex: '0 0 100%',
            },
        },
    },
    hoveredElement: {
        visibility: 'hidden',
        '$root:hover &': {
            visibility: 'visible',
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    title: {
        fontSize: '1.25em',
        flex: '1 1 auto',
    },
    content: {
        '&+&': {
            marginTop: theme.spacing(4),
            '$collapsed &': {
                marginTop: 0,
            },
        },
        '&:empty': {
            display: 'none',
        },
    },
    htmlContent: {
        '$collapsed &': {
            maxHeight: 200,
            overflow: 'hidden',
            position: 'relative',

            '&:before': {
                content: '""',
                width: '100%',
                height: '100%',
                position: 'absolute',
                left: 0,
                top: 0,
                background: 'linear-gradient(transparent 120px, white)',
            },
        },
    },
    linkContent: {
    },
    taskForm: {
    },
    collapsed: {
    },
    expanded: {
    },
    accepted: {
    },
    rejected: {
    },
    selected: {
    },
});

export const getStylesFromSettings = settings => Object.keys(settings || {}).reduce((prev, curr) => (
    curr.startsWith('style.') ? {
        ...prev,
        [curr.substring(6)]: settings[curr],
    } : prev), {});

class TaskElement extends React.Component {
    scrolled = false;

    componentDidMount() {
        this.checkScroll();
    }

    componentDidUpdate() {
        this.checkScroll();
    }

    checkScroll = () => {
        if (window?.location?.hash && this.props.element) {
            if (`${this.props.element.id}` === window.location.hash.substring(1)) {
                const el = document.getElementById(`${contentElemenIdPrefix}${window.location.hash.substring(1)}`);
                if (el) {
                    this.scrolled = true;
                    setTimeout(() => {
                        el.scrollIntoView();
                        // el.scrollIntoView({ behavior: 'smooth' });
                    }, 200);
                }
            }
        }
    }

    getLinkedElementReference = (settings) => {
        if (!settings?.linkedElementTaskId) {
            return null;
        }

        const refTask = this.props.getProgramItem(settings.linkedElementTaskId);
        const refElement = refTask.contentElements?.find(e => e.id === Number(settings.linkedElementElementId));
        const refTaskResponses = this.props.getSpecificTaskResponses(settings.linkedElementTaskId);

        return {
            element: refElement,
            taskResponses: refTaskResponses,
        };
    }

    render() {
        const {
            classes, translations, task, taskResponses, element, TaskFormProps, customElementHandler, getSpecificTaskResponses, showValidationErrors, className,
            onResponse, LinkElementProps, getProgramItem, noHiddenStates, HoveredElement, selectedElementId, addNoContextForImages, elementPreContent, elementIndex,
        } = this.props;

        const contentState = getElementConditionState(element, getSpecificTaskResponses, task, getProgramItem);
        const customHandled = customElementHandler && customElementHandler(element, contentState);
        const linkedElementReference = this.getLinkedElementReference(element.settings);

        if (customHandled) {
            return customHandled;
        }

        if (contentState === elementConditionStates.hidden && !noHiddenStates) {
            return null;
        }

        return (
            <>
                {
                    elementPreContent && elementPreContent(element, elementIndex)
                }
                <div
                    id={`${contentElemenIdPrefix}${element.id}`}
                    className={classnames(classes.root, className, {
                        [classes.accepted]: contentState === elementConditionStates.accepted,
                        [classes.rejected]: contentState === elementConditionStates.rejected,
                        [classes.collapsed]: contentState === elementConditionStates.collapsed,
                        [classes.expanded]: contentState === elementConditionStates.expanded,
                        [classes.selected]: selectedElementId && element.id === selectedElementId,
                        [`et_${element.elementType}`]: element.elementType && element.elementType !== systemContentElementTypes.default,
                    })}
                    style={getStylesFromSettings(element.settings)}
                >
                    {HoveredElement && (
                        <HoveredElement taskId={task.id} elementId={element.id} className={classes.hoveredElement} />
                    )}

                    {element.title && (
                        <div className={classes.header}>
                            <Typography variant="subtitle2" className={classes.title}>
                                {element.title}
                            </Typography>
                        </div>
                    )}

                    {linkedElementReference && (
                        <div className={classnames(classes.content, classes.taskForm)}>
                            <TaskForm
                                element={linkedElementReference.element}
                                taskResponses={linkedElementReference.taskResponses}
                                contentState={contentState}
                                onResponse={() => {}}
                                translations={translations}
                                getProgramItem={getProgramItem}
                                getSpecificTaskResponses={getSpecificTaskResponses}
                                taskId={task.id}
                                readOnly
                            />
                        </div>
                    )}

                    <HtmlContent html={element.htmlContent} className={classnames(classes.content, classes.htmlContent)} addNoContextForImages={addNoContextForImages} />

                    <TaskElementLink link={element.link} className={classnames(classes.content, classes.linkContent)} translations={translations} {...LinkElementProps} />

                    <div className={classnames(classes.content, classes.taskForm)}>
                        <TaskForm
                            element={element}
                            taskResponses={taskResponses}
                            contentState={contentState}
                            onResponse={onResponse}
                            translations={translations}
                            getProgramItem={getProgramItem}
                            getSpecificTaskResponses={getSpecificTaskResponses}
                            taskId={task.id}
                            showValidationErrors={showValidationErrors}
                            {...TaskFormProps}
                        />
                    </div>
                </div>
            </>
        );
    }
}

TaskElement.propTypes = {
    classes: PropTypes.object.isRequired,
    translations: PropTypes.object,
    element: PropTypes.object.isRequired,
    task: PropTypes.object.isRequired,
    TaskFormProps: PropTypes.object,
    customElementHandler: PropTypes.func,
    onResponse: PropTypes.func.isRequired,
    LinkElementProps: PropTypes.object,
    taskResponses: PropTypes.object,
    getProgramItem: PropTypes.func.isRequired,
    getSpecificTaskResponses: PropTypes.func.isRequired,
    noHiddenStates: PropTypes.bool,
    HoveredElement: PropTypes.object,
    selectedElementId: PropTypes.number,
    addNoContextForImages: PropTypes.bool,
    elementIndex: PropTypes.number.isRequired,
    elementPreContent: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    className: PropTypes.string,
};

TaskElement.defaultProps = {
    TaskFormProps: null,
    customElementHandler: null,
    LinkElementProps: null,
    translations: null,
    taskResponses: null,
    noHiddenStates: false,
    HoveredElement: null,
    selectedElementId: null,
    addNoContextForImages: false,
    elementPreContent: null,
    showValidationErrors: false,
    className: null,
};


export default withStyles(styles)(TaskElement);
